import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArtistApplications } from '../../services/adminService';

const PAGE_LIMIT = 20;

const STATUS_CONFIG = {
  pending:  { label: 'Pendiente',  color: '#f0a500', bg: '#fef9e7' },
  approved: { label: 'Aprobada',   color: '#28a745', bg: '#eaf7ed' },
  rejected: { label: 'Rechazada',  color: '#dc3545', bg: '#fde8ea' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'medium' });

const AdminArtistApplicationsPage = () => {
  const { session } = useAuth();
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    getArtistApplications(session.access_token, { page, limit: PAGE_LIMIT })
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page]);

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Solicitudes de artista
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} solicitud{total !== 1 ? 'es' : ''}
        </span>
      </div>

      {/* Error */}
      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Artista', 'Solicitante', 'Enviada', 'Estado', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>
                    No hay solicitudes que coincidan.
                  </td>
                </tr>
              ) : applications.map((app) => (
                <tr key={app.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontWeight: '500' }}>{app.name}</div>
                    {app.slug && <div style={{ color: '#888', fontSize: '0.82rem' }}>@{app.slug}</div>}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{app.customerEmail}</td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', color: '#555' }}>{formatDate(app.createdAt)}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <StatusBadge status={app.status} />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <Link
                      to={`/admin/artist-applications/${app.id}`}
                      style={{ color: '#2ca9e1', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                    >
                      Ver →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminArtistApplicationsPage;
